<script setup lang="ts">
import { computed, onMounted, ref, watch, nextTick, watchEffect } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { useTeamStore, useUserStore, type TeamDto } from '@blueprint/frontend-basics'

import ShopCreationProgress from '@/components/molecules/ShopCreationProgress.vue'
import ShopCreationForm from '@/components/molecules/ShopCreationForm.vue'
import { AppRoutes } from '@/enums/app-routes.enum.ts'
import type { ShopDto } from '@/models/dtos/shop.dto.ts'
import { useServiceStore } from '@/stores/service.store.ts'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const userStore = useUserStore()
const teamStore = useTeamStore()
const serviceStore = useServiceStore()

const emit = defineEmits<{
  'title-change': [title: string]
  'signup-required': [payload: { shopName: string }]
}>()

const props = defineProps<{
  initialShopName?: string
  autoCreate?: boolean
}>()

const shopName = ref('')
const isCreating = ref(false)
const progressRef = ref<InstanceType<typeof ShopCreationProgress> | null>(null)
const selectedTeam = ref<TeamDto | null>(null)
const teamsLoading = ref(false)
const checkingShops = ref(false)
const existingShop = ref<ShopDto | null>(null)
const autoCreateRequested = ref(false)
const initializing = ref(true)
const navigatingToManagement = ref(false)

const hasTeams = computed(() => teamStore.teamsAvailable.length > 0)
const canCreateShop = computed(
  () =>
    shopName.value.trim().length > 0 &&
    !isCreating.value &&
    !existingShop.value,
)

const welcomeTitle = computed(() => t('home.welcome'))

const redirectToShopManagement = (team: TeamDto, shop: ShopDto): void => {
  if (!team.id || !shop.id) return
  navigatingToManagement.value = true
  const lang = typeof route.params.lang === 'string' ? route.params.lang : 'en'
  void router.replace({
    name: AppRoutes.SHOP_MANAGEMENT,
    params: { lang, teamId: team.id, shopId: shop.id },
  })
}

onMounted(async () => {
  if (props.initialShopName) {
    shopName.value = props.initialShopName
  }
  autoCreateRequested.value = !!props.autoCreate

  try {
    await userStore.awaitInit()
    if (!userStore.isAnonymous) {
      await loadTeamsAndSetDefaults()
    }
  } finally {
    initializing.value = false
  }
})

watchEffect(() => {
  const ready =
    autoCreateRequested.value &&
    userStore.isLoggedInAndValid &&
    !initializing.value &&
    shopName.value.trim().length > 0 &&
    !isCreating.value &&
    !teamsLoading.value &&
    !checkingShops.value &&
    !existingShop.value &&
    !navigatingToManagement.value

  if (ready) {
    autoCreateRequested.value = false
    void handleCreateClick()
  }
})

watch(welcomeTitle, (title) => emit('title-change', title), { immediate: true })

const loadTeamsAndSetDefaults = async (): Promise<void> => {
  try {
    teamsLoading.value = true
    if (teamStore.teamsAvailable.length === 0) {
      await teamStore.loadTeams(serviceStore, 0)
    }
    const teams = teamStore.teamsAvailable
    if (teams.length === 0) {
      return
    }
    if (teamStore.selected) {
      selectedTeam.value = teamStore.selected
    } else {
      selectedTeam.value = teams[0]
      await teamStore.selectTeam(serviceStore, selectedTeam.value)
    }
    if (selectedTeam.value) {
      await checkTeamShops(selectedTeam.value)
    }
  } catch (error) {
    console.error('Error loading teams:', error)
  } finally {
    teamsLoading.value = false
  }
}

const checkTeamShops = async (team: TeamDto): Promise<void> => {
  if (!team.id) return

  try {
    checkingShops.value = true
    const shopsResponse = await serviceStore.shopService.getShopsByTeam(
      team.id,
      0,
      1,
    )
    const shop =
      shopsResponse.items && shopsResponse.items.length > 0
        ? shopsResponse.items[0]
        : null
    existingShop.value = shop ?? null
    if (shop) {
      redirectToShopManagement(team, shop)
    }
  } catch (error) {
    console.error('Error checking team shops:', error)
    existingShop.value = null
  } finally {
    checkingShops.value = false
  }
}

const handleProgressCompleted = ({
  team,
  shop,
}: {
  team: TeamDto
  shop: ShopDto
}): void => {
  selectedTeam.value = team
  existingShop.value = shop
  isCreating.value = false
  redirectToShopManagement(team, shop)
}

const handleProgressError = (): void => {
  setTimeout(() => {
    isCreating.value = false
  }, 1000)
}

const handleCreateClick = async (): Promise<void> => {
  if (userStore.isAnonymous) {
    emit('signup-required', { shopName: shopName.value.trim() })
    return
  }

  isCreating.value = true
  await nextTick()
  await progressRef.value?.start()
}
</script>

<template>
  <div>
    <div
      v-if="initializing || navigatingToManagement"
      class="text-center py-8"
    >
      <v-progress-circular indeterminate color="primary" class="mb-4" />
      <p class="text-body-2">
        {{ t(initializing ? 'home.loadingSession' : 'home.openingShopManagement') }}
      </p>
    </div>

    <div v-else>
      <ShopCreationProgress
        v-if="isCreating"
        ref="progressRef"
        :shop-name="shopName"
        :has-teams="hasTeams"
        :selected-team="selectedTeam"
        @title-change="emit('title-change', $event)"
        @completed="handleProgressCompleted"
        @error="handleProgressError"
      />

      <ShopCreationForm
        v-else
        :shop-name="shopName"
        :can-create="canCreateShop"
        :teams-loading="teamsLoading"
        :checking-shops="checkingShops"
        :has-teams="hasTeams"
        :team-name="selectedTeam?.name"
        @update:shop-name="shopName = $event"
        @create="handleCreateClick"
      />
    </div>
  </div>
</template>
